import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Container, Row, Col } from 'reactstrap'
import { Link } from 'gatsby'
import styled from 'styled-components'

import { useUserContext } from '~providers/user'
import scssVars from '~components/scss-variables'
import { Breadcrumbs, MobileBreadcrumb } from '~components/Services/Intro'
import { ReadMoreToggle, ShippingInfoPopup } from '~components/Modals/ShippingPopup'

const StyledContainer = styled(Container)`
  max-width: 1280px !important;
`

const PageHeading = styled.h1`
  color: ${scssVars.darkBrown};
  font-family: 'Montserrat', sans-serif;
  font-weight: 300;
  text-align: center;
  font-size: 32px;
  span {
    font-weight: 500;
    color: ${scssVars.orange};
    display: block;
    font-size: 1.25em;
  }
  @media only screen and (min-width: ${scssVars.desktopMin}) {
    font-size: 36px;
  }
`

const SectionHeading = styled.h2`
  font-weight: 400;
  color: ${scssVars.darkBrown};
  text-align: center;
  display: block;
  flex-wrap: wrap;
  display: flex;
  margin-bottom: 20px;
  line-height: 1.25;
  flex-direction: column;
  @media only screen and (min-width: ${scssVars.desktopMin}) {
    display: flex;
    flex-direction: row;
    text-align: left;
  }
`

const SectionHeadingText = styled.span`
`

const SectionSpan = styled.span`
  border: 1px solid ${scssVars.brown};
  height: 1px;
  margin: 0 auto 40px;
  width: 25%;
  display: block;
  margin: 20px auto;
  height: 1px;
  @media only screen and (min-width: ${scssVars.desktopMin}) {
    margin-left: 30px;
    margin-bottom: 0;
    flex-grow: 1;
  }
`

const StepHeading = styled.h4`
  font-family: 'Montserrat', sans-serif;
  display: inline;
  font-size: 16px;
  font-weight: 800;

  @media only screen and (min-width: ${scssVars.desktopMin}) {
    color: ${scssVars.brightOrange};
    display: block;
    font-size: 24px;
    span {
      color: ${scssVars.darkBrown};
      font-size: 0.75em;
    }
  }
`

const Bolded = styled.h6`
  color: ${scssVars.darkBrown};
  font-weight: 800;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  line-height: 1.25 !important;
`

const Regular = styled.p`
  color: ${scssVars.darkBrown};
  letter-spacing: 0.54px;
  font-family: "EB Garamond", serif;
  font-size: 16px;
`

const CTAButton = styled(Link)`
  && {
    width: 380px;
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.5);
    max-width: 90%;
    pointer-events: ${props => props.disabled ? 'none' : 'auto'};
    opacity: ${props => props.disabled ? '0.65' : '1'};
    color: ${props => props.disabled ? '#212529' : 'auto'};
  }
`

const PoweredByText = styled.p`
  text-align: center;
  margin-top: 12px;
`

const Banner = styled.div`
  box-sizing: border-box;
  background-image: url('https://leatherspa.imgix.net/LS_Website-Redesign_Mobile-Banner_01.2@3x.png?fit=scale&w=992&fm=jpg');
  background-size: cover;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 300px;
  @media only screen and (min-width: ${scssVars.desktopMin}) {
    background-image: url('https://leatherspa.imgix.net/Start_A_Repair_Desktop_Banner.png?fit=scale&w=1360&fm=jpg');
    height: 490px;
  }

  ${CTAButton} {
    margin: 0 auto;
  }
`

const Breadbox = styled.div`
  padding: 20px 0 10px 0;

  ul, div {
    margin-left: 0;
  }
  a {
    color: ${scssVars.darkBrown};
  }

  ${MobileBreadcrumb} {
    margin-left: 0;
    padding: 5px 0 5px 0;
  }

  @media only screen and (max-width: ${scssVars.tabletMax}) {
    padding-bottom: 10px;
  }
`

const WhyChooseUsSection = styled.div`
  background-color: ${scssVars.gray};
  width: 100%;
`

const ScrollCallToAction = styled.a`
  position: relative;
  margin: 0 auto;
  height: 90px;
  font-size: 13px;
  font-weight: bold;
  color: ${scssVars.orange};
  margin-top: 11px;

  &:hover {
    text-decoration: none;
    color: ${scssVars.orange};
  }

  @keyframes arrow {
    0% {
      top: 10px;
    }
    50% {
      top: 20px;
    }
    100% {
      top: 10px;
    }
  }

  &:after {
    border-right: 2px solid ${scssVars.orange};
    border-bottom: 2px solid ${scssVars.orange};
    transform: rotate(45deg);

    content: '';
    width: 40px;
    height: 40px;
    position: absolute;
    margin: auto;
    top: 10px;
    right: 0;
    left: 0;
    animation: 3s arrow infinite ease;
  }

  @media only screen and (max-width: ${scssVars.tabletMax}) {
    display: none;
  }
`

const Anchor = styled.div`
  position: relative;
  top: -70px;
`

const DeliveryImage = ({ src, srcMobile, className }) => {
  return (
    <picture className={className}>
      <source srcSet={src} media={`(min-width: ${scssVars.desktopMin})`} />
      <div className="d-none d-lg-block">
        <img className="w-100 px-3 pb-3" src={src} />
      </div>
      <div className="d-lg-none">
        <img className="w-50" src={srcMobile} />
      </div>
    </picture>
  )
}

DeliveryImage.propTypes = {
  src: PropTypes.string.isRequired,
  srcMobile: PropTypes.string,
  className: PropTypes.string
}

function RepairIntro () {
  const { isAuthenticated } = useUserContext()
  const [modalContentType, setModalContentType] = useState('')

  return (
    <main>
      <ShippingInfoPopup contentId={modalContentType} />

      <Banner>
        <PageHeading>
          Leather Spa
          <span>Repair Wizard</span>
        </PageHeading>
        <PoweredByText>Powered by LS Technologies</PoweredByText>
        <ScrollCallToAction href="#how-it-works">
          Scroll to Start
        </ScrollCallToAction>
      </Banner>
      <Anchor id="how-it-works" />
      <StyledContainer>
        <Breadbox>
          <Breadcrumbs>
            <li>
              <Link to="/">Home</Link>
            </li>
            <li>Repair Wizard</li>
          </Breadcrumbs>
          <MobileBreadcrumb>Home</MobileBreadcrumb>
        </Breadbox>
        <SectionHeading className="mt-2">
          <SectionHeadingText>How It Works</SectionHeadingText>
          <SectionSpan />
        </SectionHeading>
        <Row className="mb-5">
          <Col lg={4} className="mb-2 mb-lg-0">
            <StepHeading>
              Step 1
              <br className="d-none d-lg-block" />
              <span className="d-lg-none">:&nbsp;</span>
              <span>Describe Your Item</span>
            </StepHeading>
            <Regular>
              Tell us what needs repair. Is it your favorite pair of shoes, handbag, wallet, belt or garment? Maybe even something unusual you never thought could be repaired? We can handle practically anything.
            </Regular>
          </Col>
          <Col lg={4} className="mb-2 mb-lg-0">
            <StepHeading>Step 2
              <br className="d-none d-lg-block" />
              <span className="d-lg-none">:&nbsp;</span>
              <span>Choose The Repair Service</span>
            </StepHeading>
            <Regular>
              Let us know what you would like done. Choose from our menu of services or tell us about your ideas for customization and personalization.
            </Regular>
          </Col>
          <Col lg={4}>
            <StepHeading>
              Step 3
              <br className="d-none d-lg-block" />
              <span className="d-lg-none">:&nbsp;</span>
              <span>Pick A Shipping Mode</span>
            </StepHeading>
            <Regular>
              Choose the most convenient way for you to send us your item, drop it off, or have us pick it up.
              Follow the whole process and the status of your repairs on your personal digital repair dashboard in your account.
            </Regular>
          </Col>
        </Row>
      </StyledContainer>
      <WhyChooseUsSection className="pt-5 pb-4">
        <StyledContainer>
          <SectionHeading>
            <SectionHeadingText>Why Choose Us?</SectionHeadingText>
            <SectionSpan />
          </SectionHeading>
          <Row>
            <Col md={{ size: 6 }} lg={{ size: 3 }}>
              <Bolded>Leather Care Made Easy</Bolded>
              <Regular>
              We offer free repair quotes, in-home pickup, and a simple mail-in process all via our online platform.
              </Regular>
            </Col>
            <Col md={{ size: 6 }} lg={{ size: 3 }}>
              <Bolded>Quality Repairs And Service</Bolded>
              <Regular>
                We guarantee that your items will be handled with the utmost care and expertise, and we are dedicated to total customer satisfaction.
              </Regular>
            </Col>
            <Col md={{ size: 6 }} lg={{ size: 3 }}>
              <Bolded>Peace of Mind</Bolded>
              <Regular>
                Know that your valuables are safe and secure from the beginning to the end of the repair process while they are in our care.
              </Regular>
            </Col>
            <Col md={{ size: 6 }} lg={{ size: 3 }}>
              <Bolded>It's Sustainable</Bolded>
              <Regular>
                Repairing extends the life cycle of luxury items that you already own and love, thus doing our part to contribute to a more sustainable fashion industry.
              </Regular>
            </Col>
          </Row>
        </StyledContainer>

      </WhyChooseUsSection>
      <section className="text-center my-5">
        <StyledContainer>
          <SectionHeading className="d-none d-lg-flex">
            <span>Three Options</span>
            <SectionSpan />
            <div className="w-100">To Get Your Items To Us</div>
          </SectionHeading>
          <SectionHeading className="d-lg-none">
            <SectionHeadingText>3 Options To Get Your Items To Us</SectionHeadingText>
            <SectionSpan />
          </SectionHeading>
          <Row>
            <Col lg={4} className="px-lg-5">
              <DeliveryImage
                src={'https://leatherspa.imgix.net/LS_Repair-Wizard_Box.jpg?fit=scale&w=768'}
                srcMobile={'https://leatherspa.imgix.net/LS_Repair-Wizard_Box.jpg?fit=scale&w=768'}
              />
              <Bolded>Mail-in</Bolded>
              <Regular>
                We accept mail-in repairs worldwide and even from our own backyard.
                Simply send us your items if you can’t bring them in-store.
              </Regular>
              <ReadMoreToggle setModalContentType={() => setModalContentType('mailIn')}>
                NEW! Drop off at The UPS Store.
              </ReadMoreToggle>
            </Col>

            <Col lg={4} className="px-lg-5">
              <DeliveryImage
                src={'https://leatherspa.imgix.net/LS_Repair-Wizard_Van.jpg?fit=scale&w=768'}
                srcMobile={'https://leatherspa.imgix.net/LS_Repair-Wizard_Van.jpg?fit=scale&w=768'}
              />
              <Bolded>Schedule A Pick-up</Bolded>
              <Regular>
                We offer pick-up services to most of Manhattan, much of Brooklyn, and Long Island City, Queens.
              </Regular>
              <ReadMoreToggle setModalContentType={() => setModalContentType('pickUp')}>
                NEW! ON-DEMAND pick-up service.
              </ReadMoreToggle>
            </Col>

            <Col lg={4} className="px-lg-5">
              <DeliveryImage
                srcMobile={'https://leatherspa.imgix.net/LS_Repair-Wizard_Map.jpg?fit=scale&w=768'}
                src={'https://leatherspa.imgix.net/LS_Repair-Wizard_Map.jpg?fit=scale&w=768'}
              />
              <Bolded>Drop-off</Bolded>
              <Regular>
                Experience our superb customer service and leather expertise in person at one of our multiple NYC locations.
              </Regular>
              <ReadMoreToggle setModalContentType={() => setModalContentType('dropOff')}>
                NEW! Express Drop Off Service
              </ReadMoreToggle>
            </Col>
          </Row>
          <div className="my-5">
            <CTAButton
              className="btn btn-primary mb-3"
              state={!isAuthenticated() ? { from: '/repair-wizard' } : {} }
              to={isAuthenticated() ? '/repair-wizard' : '/log-in'}
            >
              {isAuthenticated() ? 'Start a Repair' : 'Let\'s Get Started'}
            </CTAButton>
          </div>
        </StyledContainer>
      </section>
    </main>
  )
}

RepairIntro.propTypes = {
  toggleForm: PropTypes.func
}

export default RepairIntro
